<template lang="pug">
#kennel-modal.card-base(@click.stop)
	.header
		.header-3 Kennel Availability
		button.button-close(@click="$emit('close')")
			include ../resources/icons/close.pug
	.subheader
		.effective Effective:
		//- .start {{ new Date(date.year, date.month, date.day).toLocaleDateString() }} {{ date.month + 1 }}/{{ date.day }}
		.start {{ new Date(date.year, date.month, date.day).toLocaleDateString() }}
			include ../resources/icons/calendar_month.svg
	.size-container(v-for="size in Dogspaces.Sizes" :key="size")
		span(:class="size") {{ Dogspaces.SizeTitle[size] }}
		.kennels
			.dogs
				input(v-model="givenCounts[size]" type="text")
				.label Dogs
			.divider /
			.total
				input(v-model="givenMax[size]" type="text")
				.label Total
		.available {{ givenMax[size] - givenCounts[size] }} Available
		//- .bulk-edit(v-if="(givenMax[size] - givenCounts[size]) > 0" @click="bulkEdit = size")
		//- 	include ../resources/icons/bulk-edit.svg
	//- .customer-warning ** Customers will be able book up to 10 days of boarding without the need of approval
	.save-button
		button.secondary.outline(:disabled="!hasChanged" @click="doUpdate") Save
	BulkEdit(v-if="bulkEdit" :dog-type="bulkEdit" :from-date="new Date(date.year, date.month, date.day)" @close="bulkEdit = null")
</template>
<script setup lang="ts">
import { useKennelCount } from '@/store/kennelCounts';
import { Dogspaces, Kennels } from '@busy-human/k9-library';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { isEqual } from 'lodash';
import BulkEdit from './bulk-edit.vue';
import { events } from '@/events';
import { Temporal } from 'temporal-polyfill';

const props = defineProps<{
	date: {
		day: number, month: number, year: number
	}
}>();

const kennelStore = useKennelCount();

const storeKey = computed(() => kennelStore.kennelCountKey(props.date.year, props.date.month+1, props.date.day));

const bulkEdit = ref<null | Dogspaces.Sizes>(null);

const givenCounts = ref(Kennels.Collection.defaults().kennelCount);
const givenMax = ref(Kennels.Collection.defaults().kennelCount);

function loadCounts() {
	givenCounts.value = {...(kennelStore.kennelCount.get(storeKey.value) || Kennels.Collection.defaults().kennelCount)};
	givenMax.value = {...(kennelStore.kennelMax.get(storeKey.value) || Kennels.Collection.defaults().kennelCount)};
}

onMounted(() => events.on("kennel:reload", loadCounts));
onUnmounted(() => events.off("kennel:reload", loadCounts));

watch(() => props.date, () => {
	loadCounts();
}, {immediate: true});

const hasChanged = computed(() => {
	const currentCounts = kennelStore.kennelCount.get(storeKey.value) || Kennels.Collection.defaults().kennelCount;
	const currentMax = kennelStore.kennelMax.get(storeKey.value) || Kennels.Collection.defaults().kennelCount;

	return (!isEqual(currentCounts, givenCounts.value) || !isEqual(currentMax, givenMax.value));
});

type Emits = {
	(e: 'close'): void
}

const emit = defineEmits<Emits>();

async function doUpdate() {
	await kennelStore.adjustKennelCounts(
		kennelStore.kennelCountKey(props.date.year, props.date.month+1, props.date.day),
		givenCounts.value,
		givenMax.value
	);
	emit('close');
	events.emit("kennel:reload");
}

</script>
<style lang="scss">
// Styles

#kennel-modal {
	position: absolute;
	top: 50%;
	left: 50%;
	z-index: 15;
	display: block;
	width: fit-content;
	min-width: 34.5rem;
	height: fit-content;
	padding-bottom: 2.5rem;
	outline: 1px solid $color-black;
	transform: translate(-50%, -50%);
}

#kennel-modal {
	.header {
		width: 100%;
		padding: 0 0 0.5rem;
		text-align: center;

		.header-3 {
			text-transform: none;
		}
	}

	.subheader {
		display: flex;
		align-items: center;
		justify-content: center;

		.effective {
			padding-right: 1em;
			font-weight: bold;
		}

		.start {
			display: flex;
			padding: 0 1em;
		}

		.end {
			display: flex;
			padding: 0 1em;
		}
	}

	button.button-close {
		top: 1rem;
		right: unset;
		bottom: unset;
		left: 1rem;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: unset;

		.icon {
			width: 1.5rem;
			height: 1.5rem;
		}
	}

	.size-container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1em;
		margin: 1em 0;
		border: black 1px solid;
		border-radius: 0.25rem;

		span {
			padding: 0.5rem;
			border-radius: 0.25rem;
		}

		.small {
			background-color: #99DEFF;
		}

		.medium {
			background-color: #AC97FF;
		}

		.large {
			background-color: #E497FF;
		}

		.x-large {
			background-color: #FF9399;
		}

		.xx-large {
			background-color: #F3FFA4;
		}

		.kennels {
			display: flex;

		}

		.dogs,
		.total,
		.divider {
			display: flex;
			flex-wrap: wrap;
			gap: 10px 10px;
			align-items: center;
		}

		.divider {
			padding: 0 1em;
		}

		input {
			width: 5em;
			height: 2em;
			text-align: center;
			background-color: #F0F0F0;
			border: none;
			border-radius: 0.25rem;
		}
	}

	.customer-warning {
		padding: 1em;
	}

	.save-button {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin-top: 1rem;

		button {
			width: 13rem;
		}
	}
}

@media screen and (width <=850px),
(height <=950px) {
	#kennel-modal {
		top: 50%;
		left: 50%;
		width: calc(100vw - 2rem);
		min-width: unset;

		.header {
			padding: unset;
			margin: 0 0 0.5rem;
		}

		.header-4 {
			font-size: 1.25rem;
		}

	}
}
</style>